/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { type ComponentType, type FC } from 'react';
import { SERVICE_DESK_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { globalLayout } from '@jira/platform__layouts-global-layout/src/index.tsx';
import { AsyncJsmCustomDomainChangeBoardingFlag } from '@jira/servicedesk__custom-domain-change-boarding/src/ui/async.tsx';
import { AdaptiveLanguageGlobal } from '@jira/servicedesk__adaptive-language/src/AdaptiveLanguageGlobal.tsx';
import { projectLayout } from '@jira/platform__layouts-project-layout/src/index.tsx';
import {
	composeLayouts,
	withProjectContext,
	type Layout,
} from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { lazyAfterPaint, lazy } from '@atlassian/react-loosely-lazy';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import ProjectBuilderCrossFlowSourceTrigger from './ui/project-builder/ProjectBuilderCrossFlowSourceTrigger.tsx';
import { useOnboardingPreload } from './ui/onboarding/useOnboardingPreload.tsx';
import { useJsmRightSidebarsPreload } from './ui/right-sidebars/useJsmRightSidebarsPreload.tsx';

// TODO Remove this whenever all service project route paths are no longer ambiguous
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withServiceProjectContext = (Component: ComponentType<Record<any, any>>) =>
	withProjectContext(Component, {
		project: {
			style: 'any',
			type: SERVICE_DESK_PROJECT,
		},
	});

const AdaptiveLanguageGlobalGated = componentWithFG(
	'jsm_adaptive_language_enabled',
	withServiceProjectContext(AdaptiveLanguageGlobal),
	() => null,
);
AdaptiveLanguageGlobalGated.displayName = 'AdaptiveLanguageGlobalGated';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ProjectBuilder = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-service-project-builder" */ './ui/project-builder/ProjectBuilderWithQueryParam.tsx'
		),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const HrSpaceProjectOverlay = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-hr-space-project-ui" */ './ui/hr-space-overlay/HrSpaceOverlay.tsx'
		),
	{ ssr: false },
);

const HrSpaceProjectOverlayWhenGateOff: FC = (): null => null;
HrSpaceProjectOverlayWhenGateOff.displayName = 'HrSpaceProjectOverlayWhenGateOff';

const GatedHrSpaceProjectOverlay = componentWithCondition(
	() => fg('is-hrsm-space-enabled'),
	withServiceProjectContext(HrSpaceProjectOverlay),
	HrSpaceProjectOverlayWhenGateOff,
);
GatedHrSpaceProjectOverlay.displayName = 'GatedHrSpaceProjectOverlay';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Onboarding = lazy(
	() =>
		import(/* webpackChunkName: "async-service-project-onboarding" */ './ui/onboarding/index.tsx'),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const RightSidebars = lazy(
	() =>
		import(
			/* webpackChunkName: "async-service-project-right-sidebars" */ './ui/right-sidebars/RightSidebars.tsx'
		),
	{ ssr: false },
);

const OnboardingWithPreload = (): React.ReactNode => {
	const shouldRender = useOnboardingPreload(Onboarding);
	return shouldRender ? <Onboarding /> : null;
};

const OnboardingWithStatus = componentWithFG(
	'servicedesk_onboarding_quickstart_enabled',
	OnboardingWithPreload,
	Onboarding,
);
OnboardingWithStatus.displayName = 'Lazy(ui/onboarding/index.tsx)';

const RightSidebarsWithPreload = (): React.ReactNode => {
	const shouldRender = useJsmRightSidebarsPreload(RightSidebars);
	return shouldRender ? <RightSidebars /> : null;
};

const RightSidebarsWithStatus = componentWithFG(
	'servicedesk_onboarding_quickstart_enabled',
	RightSidebarsWithPreload,
	RightSidebars,
);
RightSidebarsWithStatus.displayName = 'Lazy(ui/right-sidebars/RightSidebars.tsx)';

export const serviceProjectLayout: Layout = composeLayouts(projectLayout, {
	globalComponents: [
		withServiceProjectContext(OnboardingWithStatus),
		withServiceProjectContext(AsyncJsmCustomDomainChangeBoardingFlag),
		ProjectBuilder,
		ProjectBuilderCrossFlowSourceTrigger,
		GatedHrSpaceProjectOverlay,
		AdaptiveLanguageGlobalGated,
	],
	rightSidebars: [withServiceProjectContext(RightSidebarsWithStatus)],
});

// rightSidebarsLayout is a layout with only right sidebar and do not include other global components
export const rightSidebarsLayout: Layout = composeLayouts(globalLayout, {
	rightSidebars: [RightSidebars],
});
